import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import type { AppRole } from "../../../lib/auth";
import {
  announcementStatus,
  createAnnouncement,
  deleteAnnouncement,
  listAllAnnouncements,
  publishAnnouncement,
  updateAnnouncement,
  type AnnouncementAudienceKind,
  type AnnouncementChannel,
  type AnnouncementRow,
} from "../../../lib/announcements";
import { hasAdminPermission, listAllCompetitions, type AdminCompetitionListRow } from "../../../lib/admin";
import {
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip as SharedFilterChip,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

const TZ = "America/New_York";

const CHANNEL_OPTIONS: { value: AnnouncementChannel; label: string }[] = [
  { value: "in_app", label: "In-app only" },
  { value: "email", label: "Email only" },
  { value: "both", label: "In-app + email" },
];

const AUDIENCE_OPTIONS: { value: AnnouncementAudienceKind; label: string }[] = [
  { value: "all", label: "All users" },
  { value: "role", label: "By role" },
  { value: "competition", label: "By competition" },
];

const ROLE_OPTIONS: { value: AppRole; label: string }[] = [
  { value: "student", label: "Student" },
  { value: "client", label: "Client" },
  { value: "campus_admin", label: "Campus admin" },
  { value: "admin_licensing", label: "Admin (licensing)" },
  { value: "admin_rm", label: "Admin (RM)" },
  { value: "super_admin", label: "Super admin" },
];

function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ })}`;
}

function audienceSummary(row: AnnouncementRow, compById: Map<string, string>): string {
  if (row.audience_kind === "all") return "All users";
  if (row.audience_kind === "role") return `Role · ${row.audience_role ?? "—"}`;
  const cn = row.audience_competition_id ? compById.get(row.audience_competition_id) : null;
  return cn ? `Competition · ${cn}` : `Competition · ${row.audience_competition_id?.slice(0, 8) ?? "—"}`;
}

function statusTone(s: ReturnType<typeof announcementStatus>): string {
  if (s === "published") return SAGE;
  if (s === "scheduled") return AMBER;
  return WL;
}

export default function AdminAnnouncementsScreen() {
  const [canManage, setCanManage] = useState<boolean | null>(null);
  const [rows, setRows] = useState<AnnouncementRow[]>([]);
  const [competitions, setCompetitions] = useState<AdminCompetitionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState<AnnouncementChannel>("in_app");
  const [audienceKind, setAudienceKind] = useState<AnnouncementAudienceKind>("all");
  const [audienceRole, setAudienceRole] = useState<AppRole>("student");
  const [competitionId, setCompetitionId] = useState<string>("");
  const [publishAt, setPublishAt] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const armedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const compById = useMemo(() => new Map(competitions.map((c) => [c.id, c.name])), [competitions]);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [allowed, list, comps] = await Promise.all([
        hasAdminPermission("manage_announcements"),
        listAllAnnouncements(),
        listAllCompetitions(),
      ]);
      setCanManage(allowed);
      setRows(list);
      setCompetitions(comps);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(
    () => () => {
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    },
    []
  );

  function resetForm() {
    setEditingId(null);
    setTitle("");
    setBody("");
    setChannel("in_app");
    setAudienceKind("all");
    setAudienceRole("student");
    setCompetitionId("");
    setPublishAt("");
    setFormErr(null);
  }

  function openNew() {
    resetForm();
    setModalOpen(true);
  }

  function openEdit(row: AnnouncementRow) {
    if (row.published_at) return;
    setEditingId(row.id);
    setTitle(row.title);
    setBody(row.body ?? "");
    setChannel(row.channel);
    setAudienceKind(row.audience_kind);
    setAudienceRole((row.audience_role as AppRole) ?? "student");
    setCompetitionId(row.audience_competition_id ?? "");
    setPublishAt(row.publish_at ?? "");
    setFormErr(null);
    setModalOpen(true);
  }

  function buildPayload() {
    const role = audienceKind === "role" ? audienceRole : null;
    const comp = audienceKind === "competition" ? (competitionId.trim() || null) : null;
    return {
      title: title.trim(),
      body: body.trim(),
      channel,
      audience_kind: audienceKind,
      audience_role: role,
      audience_competition_id: comp,
      publish_at: publishAt.trim() ? publishAt.trim() : null,
    };
  }

  async function onSaveDraft() {
    if (!title.trim()) return setFormErr("Title is required.");
    if (audienceKind === "competition" && !competitionId.trim()) return setFormErr("Select a competition.");
    setFormErr(null);
    setFormBusy(true);
    try {
      const p = buildPayload();
      if (editingId) {
        await updateAnnouncement(editingId, {
          title: p.title,
          body: p.body || null,
          channel: p.channel,
          audience_kind: p.audience_kind,
          audience_role: p.audience_role,
          audience_competition_id: p.audience_competition_id,
          publish_at: p.publish_at,
        });
      } else {
        await createAnnouncement({
          title: p.title,
          body: p.body,
          channel: p.channel,
          audience_kind: p.audience_kind,
          audience_role: p.audience_role ?? undefined,
          audience_competition_id: p.audience_competition_id ?? undefined,
          publish_at: p.publish_at,
        });
      }
      setModalOpen(false);
      resetForm();
      await load();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setFormBusy(false);
    }
  }

  async function onPublishNow() {
    if (!title.trim()) return setFormErr("Title is required.");
    if (audienceKind === "competition" && !competitionId.trim()) return setFormErr("Select a competition.");
    setFormErr(null);
    setFormBusy(true);
    try {
      const p = buildPayload();
      let id = editingId;
      if (id) {
        await updateAnnouncement(id, {
          title: p.title,
          body: p.body || null,
          channel: p.channel,
          audience_kind: p.audience_kind,
          audience_role: p.audience_role,
          audience_competition_id: p.audience_competition_id,
          publish_at: p.publish_at,
        });
      } else {
        id = await createAnnouncement({
          title: p.title,
          body: p.body,
          channel: p.channel,
          audience_kind: p.audience_kind,
          audience_role: p.audience_role ?? undefined,
          audience_competition_id: p.audience_competition_id ?? undefined,
          publish_at: p.publish_at,
        });
      }
      if (id) await publishAnnouncement(id);
      setModalOpen(false);
      resetForm();
      await load();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setFormBusy(false);
    }
  }

  async function onDuplicateRow(row: AnnouncementRow) {
    setErr(null);
    try {
      // Spawn a fresh draft from the published row's content. Preserve the
      // audience and channel, but reset publish_at so it doesn't carry the
      // original schedule forward.
      await createAnnouncement({
        title: `${row.title} (copy)`,
        body: row.body ?? "",
        channel: row.channel,
        audience_kind: row.audience_kind,
        audience_role: row.audience_role ?? undefined,
        audience_competition_id: row.audience_competition_id ?? undefined,
        publish_at: null,
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't duplicate the announcement.");
    }
  }

  async function onPublishRow(row: AnnouncementRow) {
    setErr(null);
    try {
      await publishAnnouncement(row.id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Publish failed");
    }
  }

  async function onDeleteRow(row: AnnouncementRow) {
    if (armedDeleteId !== row.id) {
      setArmedDeleteId(row.id);
      if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
      armedTimerRef.current = setTimeout(() => setArmedDeleteId(null), 4000);
      return;
    }
    if (armedTimerRef.current) clearTimeout(armedTimerRef.current);
    setArmedDeleteId(null);
    setErr(null);
    try {
      await deleteAnnouncement(row.id);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Delete failed");
    }
  }

  if (canManage === false) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: BLACK, padding: 32 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: WS, textAlign: "center" }}>
          You don&apos;t have permission to manage announcements.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: 24, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Programs · Announcements"
          title="Announcements"
          subtitle="Send in-app banners to targeted audiences. Email delivery queues once your provider is connected."
          actions={<AdminButton theme={THEME} label="+ New announcement" onPress={openNew} />}
        />

        {err ? <View style={{ marginBottom: 14 }}><AdminError theme={THEME} message={err} /></View> : null}

        {loading || canManage === null ? (
          <AdminLoading theme={THEME} />
        ) : rows.length === 0 ? (
          <View
            style={{
              marginTop: 18,
              padding: 22,
              borderRadius: 6,
              backgroundColor: FOREST_MID,
              borderWidth: 1,
              borderColor: BORDER,
              alignItems: "center",
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
              No announcements yet.
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Create one above to broadcast to students, clients, or a specific competition.
            </Text>
          </View>
        ) : (
          <View style={{ marginTop: 18, gap: 8 }}>
            {rows.map((row) => {
              const st = announcementStatus(row);
              const editable = !row.published_at;
              const armed = armedDeleteId === row.id;
              return (
                <View
                  key={row.id}
                  style={{
                    padding: 14,
                    borderRadius: 6,
                    backgroundColor: FOREST_MID,
                    borderWidth: 1,
                    borderColor: BORDER,
                  }}
                >
                  <Pressable
                    onPress={() => editable && openEdit(row)}
                    disabled={!editable}
                    style={Platform.OS === "web" && editable ? ({ cursor: "pointer" } as const) : null}
                  >
                    <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                      <Pill tone={statusTone(st)}>{st}</Pill>
                      <Pill tone={AMBER}>{row.channel.replace("_", " ")}</Pill>
                      <View style={{ flex: 1 }} />
                      <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                        {row.published_at
                          ? `Published ${fmtTs(row.published_at)}`
                          : row.publish_at
                            ? `Scheduled ${fmtTs(row.publish_at)}`
                            : "Draft"}
                      </Text>
                    </View>
                    <Text style={{ marginTop: 10, fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }}>
                      {row.title}
                    </Text>
                    <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
                      {audienceSummary(row, compById)}
                    </Text>
                    {row.body ? (
                      <Text
                        numberOfLines={2}
                        style={{
                          marginTop: 6,
                          fontFamily: FONTS.body,
                          fontSize: 12,
                          lineHeight: 17,
                          color: WM,
                        }}
                      >
                        {row.body}
                      </Text>
                    ) : null}
                    {editable ? (
                      <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
                        Tap to edit draft
                      </Text>
                    ) : null}
                  </Pressable>
                  <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                    {editable ? (
                      <RowAction label="Publish" tint={AMBER} onPress={() => void onPublishRow(row)} />
                    ) : (
                      <RowAction label="Duplicate as draft" onPress={() => void onDuplicateRow(row)} />
                    )}
                    <RowAction
                      label={armed ? "Click again" : "Delete"}
                      tint={RED}
                      onPress={() => void onDeleteRow(row)}
                    />
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* New / edit modal */}
      <Modal visible={modalOpen} transparent animationType="fade" onRequestClose={() => !formBusy && setModalOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <ScrollView contentContainerStyle={{ paddingVertical: 24 }} keyboardShouldPersistTaps="handled">
            <View
              style={{
                maxWidth: 640,
                width: "100%",
                alignSelf: "center",
                backgroundColor: FOREST_MID,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: BORDER,
                padding: 22,
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
                {editingId ? "Edit announcement" : "New announcement"}
              </Text>
              <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
                Compose a portal message for your audience. Email is queued — connect your provider before publishing email-only.
              </Text>

              {formErr ? (
                <Text style={{ marginTop: 12, fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{formErr}</Text>
              ) : null}

              <ModalLabel>Title</ModalLabel>
              <DarkInput value={title} onChange={setTitle} placeholder="Headline" />

              <ModalLabel>Message</ModalLabel>
              <DarkInput value={body} onChange={setBody} placeholder="Full message body" multiline />

              <ModalLabel>Channel</ModalLabel>
              <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {CHANNEL_OPTIONS.map((opt) => (
                  <PillChoice
                    key={opt.value}
                    label={opt.label}
                    active={channel === opt.value}
                    onPress={() => setChannel(opt.value)}
                  />
                ))}
              </View>
              {(channel === "email" || channel === "both") && (
                <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                  Email is queued for delivery once the provider is connected — no-op until then.
                </Text>
              )}

              <ModalLabel>Audience</ModalLabel>
              <View style={{ marginTop: 8, gap: 6 }}>
                {AUDIENCE_OPTIONS.map((opt) => (
                  <Pressable
                    key={opt.value}
                    onPress={() => setAudienceKind(opt.value)}
                    style={[
                      { flexDirection: "row", alignItems: "center", gap: 8, paddingVertical: 4 } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <View
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 7,
                        borderWidth: 1.5,
                        borderColor: audienceKind === opt.value ? AMBER : BORDER,
                        backgroundColor: audienceKind === opt.value ? AMBER : "transparent",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {audienceKind === opt.value ? (
                        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: FOREST }} />
                      ) : null}
                    </View>
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: LINEN }}>
                      {opt.label}
                    </Text>
                  </Pressable>
                ))}
              </View>

              {audienceKind === "role" ? (
                <View style={{ marginTop: 10 }}>
                  <ModalLabel>Role</ModalLabel>
                  <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                    {ROLE_OPTIONS.map((opt) => (
                      <PillChoice
                        key={opt.value}
                        label={opt.label}
                        active={audienceRole === opt.value}
                        onPress={() => setAudienceRole(opt.value)}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {audienceKind === "competition" ? (
                <View style={{ marginTop: 10 }}>
                  <ModalLabel>Competition</ModalLabel>
                  <View
                    style={{
                      marginTop: 8,
                      maxHeight: 180,
                      borderRadius: 4,
                      borderWidth: 1,
                      borderColor: BORDER,
                      overflow: "hidden",
                    }}
                  >
                    <ScrollView>
                      {competitions.map((c, idx) => {
                        const sel = competitionId === c.id;
                        return (
                          <Pressable
                            key={c.id}
                            onPress={() => setCompetitionId(c.id)}
                            style={[
                              {
                                paddingHorizontal: 12,
                                paddingVertical: 10,
                                borderTopWidth: idx === 0 ? 0 : 1,
                                borderTopColor: BORDER,
                                backgroundColor: sel ? "rgba(181,135,58,0.10)" : "transparent",
                              } as ViewStyle,
                              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                            ]}
                          >
                            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: sel ? AMBER : LINEN }}>
                              {c.name}
                            </Text>
                            {c.company_name ? (
                              <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                                {c.company_name}
                              </Text>
                            ) : null}
                          </Pressable>
                        );
                      })}
                    </ScrollView>
                  </View>
                </View>
              ) : null}

              <ModalLabel>Schedule (optional)</ModalLabel>
              <DarkInput
                value={publishAt}
                onChange={setPublishAt}
                placeholder="ISO 8601, e.g. 2026-05-01T14:00:00.000Z"
                autoCapitalize="none"
              />

              <View style={{ marginTop: 18, flexDirection: "row", flexWrap: "wrap", justifyContent: "flex-end", gap: 8 }}>
                <Pressable
                  onPress={() => {
                    if (!formBusy) {
                      setModalOpen(false);
                      resetForm();
                    }
                  }}
                  disabled={formBusy}
                  style={[
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                      borderRadius: 3,
                      borderWidth: 1,
                      borderColor: BORDER,
                    } as ViewStyle,
                    Platform.OS === "web" && !formBusy ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={() => void onSaveDraft()}
                  disabled={formBusy}
                  style={[
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                      borderRadius: 3,
                      borderWidth: 1,
                      borderColor: BORDER,
                      backgroundColor: "rgba(255,255,255,0.02)",
                    } as ViewStyle,
                    Platform.OS === "web" && !formBusy ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: WS }}>
                    Save draft
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => void onPublishNow()}
                  disabled={formBusy}
                  style={[
                    {
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                      borderRadius: 3,
                      backgroundColor: AMBER,
                      opacity: formBusy ? 0.5 : 1,
                    } as ViewStyle,
                    Platform.OS === "web" && !formBusy ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  {formBusy ? (
                    <ActivityIndicator size="small" color={FOREST} />
                  ) : (
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: FOREST }}>
                      Publish now
                    </Text>
                  )}
                </Pressable>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </ScrollView>
  );
}

function ModalLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        marginTop: 14,
        fontFamily: FONTS.bodyBold,
        fontSize: 9.5,
        letterSpacing: 1.6,
        textTransform: "uppercase",
        color: WM,
      }}
    >
      {children}
    </Text>
  );
}

// Thin wrappers route visuals through the shared brand components without
// touching the many call sites in this file.
function DarkInput({
  value,
  onChange,
  placeholder,
  multiline,
  autoCapitalize,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
}) {
  return (
    <AdminInput
      theme={THEME}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      autoCapitalize={autoCapitalize}
      multiline={multiline}
      minHeight={multiline ? 100 : undefined}
      style={{ marginTop: 6 }}
    />
  );
}

function PillChoice({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return <SharedFilterChip theme={THEME} label={label} active={active} onPress={onPress} />;
}

function Pill({ tone, children }: { tone: string; children: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: tone,
        backgroundColor: "rgba(255,255,255,0.04)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: tone,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function RowAction({ label, tint, onPress }: { label: string; tint?: string; onPress: () => void }) {
  const variant = tint === RED ? "destructive" : tint === AMBER ? "filled" : "outline";
  return <AdminButton theme={THEME} label={label} variant={variant} size="sm" onPress={onPress} />;
}
